/**
 * Memory Pool Manager — Pre-allocated Float32Arrays for zero-GC operation
 * Handles particle states, physics bodies, and transformation matrices.
 */

export class MemoryPool {
  private static instance: MemoryPool;

  // Particle Buffer: x, y, z, vx, vy, vz, life, maxLife, size, r, g, b, a (13 floats per particle)
  public static readonly FLOATS_PER_PARTICLE = 13;
  public static readonly MAX_PARTICLES = 50000;
  public particleBuffer: Float32Array;
  public activeParticleCount: number = 0;

  // Physics Bodies Buffer: px, py, pz, vx, vy, vz, mass, friction, restitution (9 floats per body)
  public static readonly FLOATS_PER_BODY = 9;
  public static readonly MAX_BODIES = 1000;
  public bodyBuffer: Float32Array;
  public activeBodyCount: number = 0;

  // Reusable Matrix & Vector Scratch buffers (32 floats)
  public scratchBuffer: Float32Array;

  private constructor() {
    this.particleBuffer = new Float32Array(MemoryPool.MAX_PARTICLES * MemoryPool.FLOATS_PER_PARTICLE);
    this.bodyBuffer = new Float32Array(MemoryPool.MAX_BODIES * MemoryPool.FLOATS_PER_BODY);
    this.scratchBuffer = new Float32Array(64);
  }

  public static getInstance(): MemoryPool {
    if (!MemoryPool.instance) {
      MemoryPool.instance = new MemoryPool();
    }
    return MemoryPool.instance;
  }

  public resetParticles(): void {
    this.activeParticleCount = 0;
  }

  public resetBodies(): void {
    this.activeBodyCount = 0;
  }

  /**
   * Spawns a particle in contiguous memory buffer
   */
  public spawnParticle(
    x: number, y: number, z: number,
    vx: number, vy: number, vz: number,
    life: number, size: number,
    r: number, g: number, b: number, a: number
  ): number {
    if (this.activeParticleCount >= MemoryPool.MAX_PARTICLES) {
      return -1; // Buffer full
    }

    const offset = this.activeParticleCount * MemoryPool.FLOATS_PER_PARTICLE;
    const buf = this.particleBuffer;

    buf[offset + 0] = x;
    buf[offset + 1] = y;
    buf[offset + 2] = z;
    buf[offset + 3] = vx;
    buf[offset + 4] = vy;
    buf[offset + 5] = vz;
    buf[offset + 6] = life;
    buf[offset + 7] = life; // maxLife
    buf[offset + 8] = size;
    buf[offset + 9] = r;
    buf[offset + 10] = g;
    buf[offset + 11] = b;
    buf[offset + 12] = a;

    return this.activeParticleCount++;
  }
}
